import { useMemo, useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';

import { fetchAuditTrails, fetchAuditTrailCount, AuditTrailRecord } from '../../../../redux/slices/audit_trailSlice';
import { RootState, useAppDispatch } from 'redux/store';

import { useTableController } from 'hooks/useTableController';
import useAdvanceTable from 'hooks/useAdvanceTable';
import TableViewLayout from 'layouts/TableViewLayout';

const ListAuditTrail = () => {
  const dispatch = useAppDispatch();

  const { records: auditTrail, pagination, config: headerConfig, filtersConfig, error, loading } = useSelector((state: RootState) => state.auditTrails);

  // Track count request to allow cancellation
  const countRequestRef = useRef<any>(null);

  const userData = useSelector((state: RootState) => state.auth.userData);
  const [showSidebar, setShowSidebar] = useState(false);
  const pageName = 'audit-trail';

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updateUserSettings,
    updatePage,
    updateLimit,
    limit,
    sortColumn,
    sortOrder,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
    payload,
    isHydrated,
  } = useTableController({
    userData,
    pageName,
    filtersConfig,
    headerConfig,
    fetchData: fetchAuditTrails,
    columnFormats: { created_at: 'datetime' },
    getFormattedRow: (entry: AuditTrailRecord) => {
      // audit_details is opaque JSONB (varies by audit_type); narrow before
      // reading the HTTP-style endpoint/status summary fields.
      const details: { endpoint?: unknown; status?: unknown } =
        typeof entry.audit_details === 'object' && entry.audit_details !== null ? (entry.audit_details as { endpoint?: unknown; status?: unknown }) : {};
      const endpoint = typeof details.endpoint === 'string' ? details.endpoint : '';
      const status = details.status !== undefined ? `(${String(details.status)})` : '';

      return {
        ...entry,
        audit_details: `${endpoint} ${status}`.trim(),
        audit_type: entry.audit_type,
      };
    },
  });

  // Payload for count: filters + sort + limit, page fixed to 1 (count is independent of page)
  const countPayload = useMemo(() => ({ ...filters, sortColumn, sortOrder, page: 1, limit }), [filters, sortColumn, sortOrder, limit]);

  // Fetch count only when filters/sort/limit change, not on page change.
  // Use filtersConfig?.length (not filtersConfig) so we don't refetch when the list response
  // assigns a new filtersConfig reference on page change.
  const filtersConfigLength = filtersConfig?.length ?? 0;
  useEffect(() => {
    if (!isHydrated || filtersConfigLength === 0) {
      return;
    }

    // Cancel previous count request if it exists
    const previousRequest = countRequestRef.current;
    if (previousRequest) {
      // RTK thunks have an abort method
      if (typeof previousRequest.abort === 'function') {
        previousRequest.abort();
      }
      countRequestRef.current = null;
    }

    // Dispatch new count request immediately
    // The rejected handler for aborted requests won't set loading to false
    const countThunk = dispatch(fetchAuditTrailCount(countPayload) as any);
    countRequestRef.current = countThunk;

    // Cleanup: abort on unmount or when dependencies change
    return () => {
      if (countRequestRef.current) {
        if (typeof countRequestRef.current.abort === 'function') {
          countRequestRef.current.abort();
        }
        countRequestRef.current = null;
      }
    };
  }, [dispatch, countPayload, isHydrated, filtersConfigLength]);

  const columns = generateColumns((key, order) => {
    updateSort(key, order);
    updateUserSettings(key, order);
  });

  const formattedData = useMemo(() => getFormattedData(auditTrail), [auditTrail]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: false,
  });

  const { current_page: currentPage, total_pages: totalPages, total_records: totalItems } = pagination;

  const isLoading = loading.fetchAuditTrails;
  const isLoadingCount = loading.fetchAuditTrailCount;

  return (
    <TableViewLayout
      filtersConfig={filtersConfig}
      filters={filters}
      pageName={pageName}
      columns={headerConfig.map((c) => ({ key: c.header_key, label: c.label }))}
      pageSize={limit}
      error={error}
      onFilterChange={setFilterAndSync}
      onResetFilters={resetFilters}
      onPageSizeChange={updateLimit}
      currentPage={currentPage}
      totalPages={totalPages}
      totalItems={totalItems}
      onPageChange={updatePage}
      table={table}
      showSidebar={showSidebar}
      setShowSidebar={setShowSidebar}
      onRefresh={refresh}
      isLoading={isLoading}
      isSilentRefresh={isSilentRefresh}
      isLoadingCount={isLoadingCount}
    />
  );
};

export default ListAuditTrail;
